import { Calendar } from 'lucide-react';

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') || url.startsWith('data:') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const photosOf = (event) => {
  if (Array.isArray(event?.photos) && event.photos.length) return event.photos;
  if (event?.photo_url) return [event.photo_url];
  return [];
};

/**
 * EventPhotoDisplay
 * Variants:
 *   - 'thumb'   : small square thumbnail (used in compact rows)
 *   - 'card'    : top-of-card hero strip (h-32 / h-40)
 *   - 'hero'    : full-width hero (used on detail page when 1 photo)
 *   - 'gallery' : horizontally scrollable gallery (used on detail page when 2+ photos)
 *
 * If no photos, renders the branded CMC placeholder with the event title.
 */
export default function EventPhotoDisplay({
  event,
  variant = 'card',
  className = '',
  testIdPrefix = 'event-photo',
}) {
  const photos = photosOf(event);

  if (variant === 'thumb') {
    if (!photos.length) {
      return (
        <div
          className={`w-14 h-14 bg-gradient-to-br from-teal/30 to-seafoam/50 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${className}`}
          data-testid={`${testIdPrefix}-placeholder-thumb`}
        >
          <span className="text-[9px] text-teal font-bold uppercase">
            {event?.event_date ? new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' }) : 'CMC'}
          </span>
          <span className="text-base font-bold text-navy leading-none">
            {event?.event_date ? new Date(event.event_date).getDate() : '·'}
          </span>
        </div>
      );
    }
    return (
      <img
        src={resolveUrl(photos[0])}
        alt={event?.title || 'Event'}
        className={`w-14 h-14 rounded-xl object-cover flex-shrink-0 ${className}`}
        data-testid={`${testIdPrefix}-thumb`}
      />
    );
  }

  // card / hero – single visual block
  if (variant === 'card' || variant === 'hero') {
    const heightClass = variant === 'hero' ? 'h-64 md:h-80' : 'h-32';
    if (!photos.length) {
      return (
        <div
          className={`relative ${heightClass} bg-gradient-to-br from-teal/30 via-seafoam/50 to-cream flex items-center justify-center overflow-hidden ${className}`}
          data-testid={`${testIdPrefix}-placeholder`}
        >
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.5), transparent 50%)',
          }} />
          <div className="relative text-center px-6">
            <Calendar className="text-teal mx-auto mb-2" size={28} />
            <p className="font-display text-navy text-sm md:text-base line-clamp-2">{event?.title || 'CaliMoms Event'}</p>
            <p className="text-[10px] uppercase tracking-widest text-teal-deep/70 mt-1">CaliMoms Connect</p>
          </div>
        </div>
      );
    }
    return (
      <img
        src={resolveUrl(photos[0])}
        alt={event?.title || 'Event'}
        className={`w-full ${heightClass} object-cover ${className}`}
        data-testid={`${testIdPrefix}-cover`}
      />
    );
  }

  // gallery: horizontal scroll
  if (variant === 'gallery') {
    if (!photos.length) {
      return (
        <div className={`h-64 md:h-80 bg-gradient-to-br from-teal/30 to-seafoam/50 flex items-center justify-center ${className}`}>
          <Calendar className="text-teal" size={32} />
        </div>
      );
    }
    return (
      <div
        className={`flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 ${className}`}
        data-testid={`${testIdPrefix}-gallery`}
      >
        {photos.map((url, idx) => (
          <img
            key={`${url}-${idx}`}
            src={resolveUrl(url)}
            alt={`${event?.title || 'Event'} — photo ${idx + 1}`}
            className="snap-start flex-shrink-0 w-full md:w-3/4 lg:w-2/3 h-64 md:h-80 object-cover rounded-2xl"
            data-testid={`${testIdPrefix}-gallery-item-${idx}`}
          />
        ))}
      </div>
    );
  }

  return null;
}
